<script setup lang="ts">
import type { InfoWithIconGridItem } from '.';

import { cn } from '@/utils';
import { isVNode } from 'vue';

import { Skeleton } from '~/components/ui/skeleton';

defineProps<{
	isLoading?: boolean;
	characteristics: InfoWithIconGridItem[];
	title?: string;
	gridClass?: string;
}>();
</script>

<template>
	<div class="flex flex-col gap-7">
		<h2 v-if="title" class="font-matter-sq text-2xl leading-9 text-primary">
			{{ title }}
		</h2>

		<div
			:class="
				cn(
					'grid grid-cols-1 gap-x-5 gap-y-6 pt-4 md:grid-cols-2 md:pt-2 xl:grid-cols-3',
					gridClass
				)
			"
		>
			<template v-if="isLoading">
				<div
					v-for="index in 6"
					:key="`property-info-section-skeleton-${index}`"
					class="flex items-start gap-3"
				>
					<Skeleton
						:is-active="isLoading"
						class="size-6 min-h-6"
						variant="white"
					/>
					<div class="flex flex-col gap-1">
						<Skeleton
							:is-active="isLoading"
							class="h-5 min-h-5 w-[162px]"
							variant="white"
						/>
						<Skeleton
							:is-active="isLoading"
							class="h-6 min-h-6 w-[140px]"
							variant="white"
						/>
					</div>
				</div>
			</template>

			<template v-else>
				<div
					v-for="(characteristic, index) in characteristics"
					:key="`property-info-${index}`"
					class="flex items-start gap-3"
				>
					<div class="flex items-center">
						<Component :is="characteristic.icon" class="size-6 text-primary" />
					</div>
					<div class="flex flex-col gap-0.5">
						<Component
							:is="characteristic.label"
							v-if="isVNode(characteristic.label)"
						/>
						<span v-else class="font-matter-sq text-[18px] text-primary">
							{{ characteristic.label }}
						</span>
						<div class="text-base font-medium text-primary">
							<Component
								:is="characteristic.description"
								v-if="isVNode(characteristic.description)"
							/>

							<template v-else-if="characteristic.description">
								{{ characteristic.description }}
							</template>
						</div>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>
